// src/pages/trading-dashboard/components/ActiveBets.jsx
import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ActiveBets = () => {
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Mock active bets data
  const activeBets = [
    {
      id: 'bet_001',
      marketTitle: 'Will Bitcoin reach $50,000 by end of 2024?',
      outcome: 'Yes',
      amount: 100,
      shares: 149,
      avgPrice: 0.67,
      currentPrice: 0.71,
      unrealizedPnL: 8.96,
      unrealizedPnLPercent: 8.96,
      placedAt: '2024-01-15T10:30:00Z',
      status: 'active',
      marketEndDate: '2024-12-31'
    },
    {
      id: 'bet_002', 
      marketTitle: 'US Presidential Election 2024 Winner',
      outcome: 'Democratic Candidate',
      amount: 250,
      shares: 481,
      avgPrice: 0.52,
      currentPrice: 0.49,
      unrealizedPnL: -14.44,
      unrealizedPnLPercent: -5.78,
      placedAt: '2024-01-10T14:22:00Z',
      status: 'active',
      marketEndDate: '2024-11-05'
    },
    {
      id: 'bet_003',
      marketTitle: 'Will Tesla stock reach $300 in 2024?',
      outcome: 'No',
      amount: 75,
      shares: 109,
      avgPrice: 0.69,
      currentPrice: 0.73,
      unrealizedPnL: 4.36,
      unrealizedPnLPercent: 5.81,
      placedAt: '2024-01-08T09:15:00Z',
      status: 'active',
      marketEndDate: '2024-12-31'
    }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatEndDate = (dateString) => {
    const endDate = new Date(dateString);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Ended';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'pending': return 'text-warning-400';
      case 'filled': return 'text-primary';
      default: return 'text-text-secondary';
    }
  };

  const getPnLColor = (pnl) => {
    return pnl >= 0 ? 'text-success' : 'text-error';
  };

  const handleCancelBet = (betId) => {
    console.log('Cancel bet:', betId);
    // In a real app, this would call an API to cancel the bet
  };

  const sortedBets = [...activeBets]?.sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'date':
        aValue = new Date(a.placedAt);
        bValue = new Date(b.placedAt);
        break;
      case 'amount':
        aValue = a?.amount;
        bValue = b?.amount;
        break;
      case 'pnl':
        aValue = a?.unrealizedPnL;
        bValue = b?.unrealizedPnL;
        break;
      case 'market':
        aValue = a?.marketTitle;
        bValue = b?.marketTitle;
        break;
      default:
        aValue = new Date(a.placedAt);
        bValue = new Date(b.placedAt);
    }

    if (typeof aValue === 'string') {
      return sortOrder === 'asc' ? aValue?.localeCompare(bValue) : bValue?.localeCompare(aValue);
    }
    
    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const totalAmount = activeBets?.reduce((sum, bet) => sum + bet?.amount, 0);
  const totalUnrealizedPnL = activeBets?.reduce((sum, bet) => sum + bet?.unrealizedPnL, 0);

  return (
    <div>
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-surface-hover rounded-trading p-4 border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-secondary">Active Bets</p>
              <p className="text-2xl font-data text-text-primary">{activeBets?.length}</p>
            </div>
            <Icon name="Clock" size={24} className="text-primary" />
          </div>
        </div>
        
        <div className="bg-surface-hover rounded-trading p-4 border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-secondary">Total Amount</p>
              <p className="text-2xl font-data text-text-primary">${totalAmount}</p>
            </div>
            <Icon name="DollarSign" size={24} className="text-primary" />
          </div>
        </div>
        
        <div className="bg-surface-hover rounded-trading p-4 border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-secondary">Unrealized P&L</p>
              <p className={`text-2xl font-data ${getPnLColor(totalUnrealizedPnL)}`}>
                ${totalUnrealizedPnL?.toFixed(2)}
              </p>
            </div>
            <Icon 
              name={totalUnrealizedPnL >= 0 ? 'TrendingUp' : 'TrendingDown'} 
              size={24} 
              className={getPnLColor(totalUnrealizedPnL)} 
            />
          </div>
        </div>
      </div>
      {/* Controls */}
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-body-semibold text-text-primary">Your Active Bets</h4>
        
        <div className="flex items-center space-x-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-1 text-sm border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary"
          >
            <option value="date">Sort by Date</option>
            <option value="amount">Sort by Amount</option>
            <option value="pnl">Sort by P&L</option>
            <option value="market">Sort by Market</option>
          </select>
          
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="p-1 border border-border rounded hover:bg-surface-hover transition-colors duration-trading-fast"
          >
            <Icon 
              name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
              size={16} 
              className="text-text-secondary" 
            />
          </button>
        </div>
      </div>
      {/* Active Bets Table */}
      <div className="space-y-3">
        {sortedBets?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Clock" size={48} className="text-text-secondary mx-auto mb-3" />
            <p className="text-text-secondary">No active bets</p>
            <p className="text-sm text-text-secondary mt-1">
              Your active prediction market bets will appear here
            </p>
          </div>
        ) : (
          sortedBets?.map((bet) => (
            <div
              key={bet?.id}
              className="bg-surface-hover border border-border rounded-trading p-4 hover:border-border-hover transition-all duration-trading-fast"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h5 className="font-body-semibold text-text-primary leading-tight mb-1 line-clamp-2">
                        {bet?.marketTitle}
                      </h5>
                      <div className="flex items-center space-x-4 text-sm text-text-secondary">
                        <span className="flex items-center">
                          <Icon name="Calendar" size={14} className="mr-1" />
                          Placed {formatDate(bet?.placedAt)}
                        </span>
                        <span className="flex items-center">
                          <Icon name="Clock" size={14} className="mr-1" />
                          Ends in {formatEndDate(bet?.marketEndDate)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <span className={`text-xs px-2 py-1 rounded ${getStatusColor(bet?.status)} bg-opacity-20`}>
                        {bet?.status?.charAt(0)?.toUpperCase() + bet?.status?.slice(1)}
                      </span>
                      <button
                        onClick={() => handleCancelBet(bet?.id)}
                        className="p-1 rounded hover:bg-error-900 hover:bg-opacity-20 transition-colors duration-trading-fast"
                        title="Cancel bet"
                      >
                        <Icon name="X" size={14} className="text-error" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
                    <div>
                      <p className="text-text-secondary">Outcome</p>
                      <p className="font-body-medium text-text-primary">{bet?.outcome}</p>
                    </div>
                    
                    <div>
                      <p className="text-text-secondary">Amount</p>
                      <p className="font-data text-text-primary">${bet?.amount}</p>
                    </div>
                    
                    <div>
                      <p className="text-text-secondary">Shares</p>
                      <p className="font-data text-text-primary">{bet?.shares}</p>
                    </div>
                    
                    <div>
                      <p className="text-text-secondary">Avg Price</p>
                      <p className="font-data text-text-primary">${bet?.avgPrice?.toFixed(3)}</p>
                    </div>
                    
                    <div>
                      <p className="text-text-secondary">Current Price</p>
                      <p className="font-data text-text-primary">${bet?.currentPrice?.toFixed(3)}</p>
                    </div>
                    
                    <div>
                      <p className="text-text-secondary">Unrealized P&L</p>
                      <div className="flex items-center space-x-1">
                        <p className={`font-data ${getPnLColor(bet?.unrealizedPnL)}`}>
                          ${bet?.unrealizedPnL?.toFixed(2)}
                        </p>
                        <span className={`text-xs ${getPnLColor(bet?.unrealizedPnL)}`}>
                          ({bet?.unrealizedPnLPercent > 0 ? '+' : ''}{bet?.unrealizedPnLPercent?.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActiveBets;